import React, { useState, useEffect, useRef } from 'react';
import { XIcon } from '@heroicons/react/outline';
import { useAuth } from '../context/AuthContext'; // Correction de l'importation de useAuth

const ProspectDetail = ({ prospect, onClose, onUpdate, onAdd, users = [] }) => {
    const modalRef = useRef();
    const [appSettings, setAppSettings] = useState({});

  useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await fetch('https://venteclead.st-info.fr/get_settings.php');
                const settings = await response.json();
                setAppSettings(settings);
            } catch (error) {
                console.error("Erreur chargement paramètres:", error);
            }
        };
        fetchSettings();
    }, []);

    // Fonction pour initialiser l'état avec des valeurs par défaut et le rendre robuste
    const getInitialState = (p) => {
        let history = p?.historique;

        // Étape 1: S'assurer que l'historique n'est pas null/undefined
        if (!history) {
            history = [];
        }
        // Étape 2: Si c'est un string, tenter de le parser en JSON
        else if (typeof history === 'string') {
            try {
                history = JSON.parse(history);
            } catch (e) {
                // En cas d'échec, on ne perd pas l'info, on l'encapsule
                console.warn("Impossible de parser l'historique, traitement comme texte simple:", history);
                history = [{ date: new Date().toISOString(), description: history }];
            }
        }

        // Étape 3: S'assurer que le résultat final est bien un tableau
        if (!Array.isArray(history)) {
            history = [];
        }

        // Faire de même pour le champ 'etat'
        let etat = p?.etat;
        if (!etat) {
            etat = [];
        } else if (typeof etat === 'string') {
            try {
                etat = JSON.parse(etat);
            } catch (e) {
                etat = [];
            }
        }
        if (!Array.isArray(etat)) {
            etat = [];
        }

        return {
            ...p,
            statut: p?.statut || 'Nouveau',
            etat_du_lead: p?.etat_du_lead || 'À traiter', // Utiliser etat_du_lead
            attribution: p?.attribution || 'Non attribué',
            date_rappel: p?.date_rappel || '',
            commentaire: p?.commentaire || '',
            historique: history, // Utiliser l'historique nettoyé
            etat: etat // Utiliser l'état nettoyé
        };
    };

    const [editableProspect, setEditableProspect] = useState(() => getInitialState(prospect));
    const [selectedComments, setSelectedComments] = useState([]);
    
    // Mettre à jour l'état si un autre prospect est sélectionné
    useEffect(() => {
        setEditableProspect(getInitialState(prospect));
    }, [prospect]);

    const handleFieldChange = (field, value) => {
        setEditableProspect(prev => ({ ...prev, [field]: value }));
    };
    
    const { user } = useAuth(); // Obtenir l'utilisateur connecté

    const handleSave = async () => {
        if (!user?.id) {
            alert("Vous devez être connecté pour effectuer cette action.");
            return;
        }

        const prospectToSave = { ...editableProspect, user_id: user.id };

        try {
            if (prospectToSave.id) { // Si le prospect a un ID, c'est une mise à jour
                await onUpdate(prospectToSave);
            } else { // Sinon, c'est un nouvel ajout
                await onAdd(prospectToSave);
            }
            onClose(); // Ferme le modal après la sauvegarde
        } catch (error) {
            console.error("Erreur lors de la sauvegarde du prospect:", error);
            alert(`La sauvegarde a échoué: ${error.message}`);
        }
    };

    const addHistoryEntry = (description) => {
        const newEntry = { date: new Date().toISOString(), description };
        const newHistory = [newEntry, ...(editableProspect.historique || [])];
        setEditableProspect(prev => ({ ...prev, historique: newHistory }));
    };

    const handleInteractionClick = (type) => {
        if (selectedComments.length === 0) {
            alert("Veuillez sélectionner au moins une observation.");
      return;
    }
        const description = `${type}: ${selectedComments.join(', ')}`;
        addHistoryEntry(description);
        setSelectedComments([]); // Réinitialiser les cases
    };
    
    const handleCheckboxChange = (comment) => {
        setSelectedComments(prev => 
            prev.includes(comment) 
                ? prev.filter(c => c !== comment) 
                : [...prev, comment]
        );
    };

    if (!prospect && !onAdd) return null; // Ne rien rendre si pas de prospect et pas d'ajout

    const statutOptions = appSettings.prospect_statut || [];
    const attributionOptions = [ 'Non attribué', ...users.map(u => u.nom) ];
    if (editableProspect.attribution && !attributionOptions.includes(editableProspect.attribution)) {
        attributionOptions.push(editableProspect.attribution);
    }
    const etatOptions = appSettings.prospect_etat || [];
    const predefinedComments = appSettings.predefined_comments || [];

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-gray-600 bg-opacity-75" onClick={onClose}></div>
      
      <div
        ref={modalRef}
        className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out"
      >
        <div className="flex flex-col h-full">
          <div className="bg-gray-50 px-4 py-4 sm:px-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Détails du Prospect</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <span className="sr-only">Fermer</span>
            <XIcon className="h-6 w-6" />
          </button>
        </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="p-4 sm:p-6 bg-gray-50">
              <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div className="space-y-4">
                    <h2 className="text-lg font-medium text-gray-800 border-b pb-2">Contact</h2>
            <div>
                      <label className="text-sm text-gray-500">Prénom</label>
                      <input
                        type="text"
                        value={editableProspect.prenom || ''}
                        onChange={(e) => handleFieldChange('prenom', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Nom</label>
                      <input
                        type="text"
                        value={editableProspect.nom || ''}
                        onChange={(e) => handleFieldChange('nom', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Mail</label>
                      <input
                        type="email"
                        value={editableProspect.email || ''}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Téléphone</label>
                      <input
                        type="tel"
                        value={editableProspect.telephone_portable || ''}
                        onChange={(e) => handleFieldChange('telephone_portable', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Adresse</label>
                      <input
                        type="text"
                        value={editableProspect.adresse || ''}
                        onChange={(e) => handleFieldChange('adresse', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Ville</label>
                      <input
                        type="text"
                        value={editableProspect.ville || ''}
                        onChange={(e) => handleFieldChange('ville', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Code Postal</label>
                      <input
                        type="text"
                        value={editableProspect.code_postal || ''}
                        onChange={(e) => handleFieldChange('code_postal', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
              </div>
                  <div className="space-y-4">
                    <h2 className="text-lg font-medium text-gray-800 border-b pb-2">Détails du Lead</h2>
            <div>
                      <label className="text-sm text-gray-500">Projet Google</label>
                      <input
                        type="text"
                        value={editableProspect.projet_google || ''}
                        onChange={(e) => handleFieldChange('projet_google', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Type de bien</label>
                      <input
                        type="text"
                        value={editableProspect.type_de_bien || ''}
                        onChange={(e) => handleFieldChange('type_de_bien', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Chauffage actuel</label>
                      <input
                        type="text"
                        value={editableProspect.chauffage_actuel || ''}
                        onChange={(e) => handleFieldChange('chauffage_actuel', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Situation</label>
                      <input
                        type="text"
                        value={editableProspect.situation || ''}
                        onChange={(e) => handleFieldChange('situation', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Projet</label>
                      <input
                        type="text"
                        value={editableProspect.projet || ''}
                        onChange={(e) => handleFieldChange('projet', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Type d'émetteurs</label>
                      <input
                        type="text"
                        value={editableProspect.type_emetteurs || ''}
                        onChange={(e) => handleFieldChange('type_emetteurs', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Surface à chauffer</label>
                      <input
                        type="text"
                        value={editableProspect.surface_chauffer || ''}
                        onChange={(e) => handleFieldChange('surface_chauffer', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Jardin</label>
                      <input
                        type="text"
                        value={editableProspect.jardin || ''}
                        onChange={(e) => handleFieldChange('jardin', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Âge du logement</label>
                      <input
                        type="text"
                        value={editableProspect.age_logement || ''}
                        onChange={(e) => handleFieldChange('age_logement', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                </div>
                <div>
                      <label className="text-sm text-gray-500">Bloctel</label>
                      <input
                        type="text"
                        value={editableProspect.bloctel || ''}
                        onChange={(e) => handleFieldChange('bloctel', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">État du lead</label>
                  <select value={editableProspect.etat_du_lead || ''} onChange={(e) => handleFieldChange('etat_du_lead', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md shadow-sm">
                    <option value="À traiter">À traiter</option>
                    <option value="Nouveau">Nouveau</option>
                    <option value="Contacté">Contacté</option>
                    <option value="À rappeler">À rappeler</option>
                    <option value="RAPPEL">RAPPEL</option>
                    <option value="Vente">Vente</option>
                    <option value="Perdu">Perdu</option>
                    <option value="Plus tard">Plus tard</option>
                    <option value="Rendez-vous pris">Rendez-vous pris</option>
                    <option value="Injoignable">Injoignable</option>
                    <option value="Ne pas déranger">Ne pas déranger</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Attribution</label>
                  <select value={editableProspect.attribution} onChange={(e) => handleFieldChange('attribution', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md shadow-sm">
                    {attributionOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de rappel</label>
                  <input type="datetime-local" value={editableProspect.date_rappel ? new Date(editableProspect.date_rappel).toISOString().slice(0, 16) : ''} onChange={(e) => handleFieldChange('date_rappel', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md shadow-sm"/>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire</label>
                <textarea value={editableProspect.commentaire} onChange={(e) => handleFieldChange('commentaire', e.target.value)} rows="4" className="w-full p-2 border border-gray-300 rounded-md shadow-sm"></textarea>
        </div>

            <div>
                <div className="space-y-4">
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <h3 className="font-medium mb-3">Ajouter une interaction</h3>
                    <div className="space-y-2 mb-4">
                      {predefinedComments.map(comment => (
                        <label key={comment} className="flex items-center">
                          <input type="checkbox" checked={selectedComments.includes(comment)} onChange={() => handleCheckboxChange(comment)} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                          <span className="ml-2 text-gray-700">{comment}</span>
              </label>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => handleInteractionClick('Appel')} className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">Appel</button>
                      <button onClick={() => handleInteractionClick('SMS')} className="flex-1 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600">SMS</button>
                      <button onClick={() => handleInteractionClick('Email')} className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600">Email</button>
                    </div>
                  </div>
                  <h2 className="text-lg font-medium text-gray-800 border-b pb-2 mt-6 mb-4">Historique</h2>
                  {editableProspect.historique && editableProspect.historique.map((entry, index) => (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
            <div>
                        <p className="text-sm text-gray-800">{entry.description}</p>
                        <p className="text-xs text-gray-500">{new Date(entry.date).toLocaleString('fr-FR')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <button 
              type="button"
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
              onClick={onClose}
            >
              Annuler
            </button>
            <button 
              type="button"
              className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
              onClick={handleSave}
            >
              Sauvegarder
            </button>
          </div>
        </div>
      </div>
        </div>
  );
};

export default ProspectDetail;